import { useAtomValue } from "jotai";
import isString from "lodash/isString";
import type { NextPageContext, PageProps } from "next";
import { memo } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import { withRelay } from "relay-nextjs";
import type { RelayComponent } from "relay-nextjs";

import type { AppBarVariant } from "~/components/app-bar/helpers";
import { useAppBarAdSlot } from "~/components/app-bar/hooks";
import { ArticleList } from "~/components/article/article-list";
import { DefaultCustomContents } from "~/components/article/article-render/consts";
import { useArticleBaseAdvertisingInfo } from "~/components/article/article-render/hooks";
import { ArticleSchema } from "~/components/article/article-schema";
import { ArticleSeo } from "~/components/article/article-seo";
import { FallbackLoading } from "~/components/fallback-loading";
import { currentArticleAtom } from "~/lib/current-item";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import type { articlePageQuery } from "~/queries/__generated__/articlePageQuery.graphql";

import { useArticleAcknowledgementGateOverlay, useTrackContentInterest } from "./hooks";
import { Article } from "./styles";

export const query = graphql`
  query articlePageQuery(
    $entityId: String!
    $entityUuid: String!
    $customContents: [CustomContentInput]
  ) {
    article(filter: { entityId: $entityId }) {
      ...articleBaseAdvertisingInfoArticle
      ...articleListArticle @arguments(customContents: $customContents)
      ...articleSeoArticle @arguments(customContents: $customContents)
      ...helpersArticleSections
      ...hooksArticleAcknowledgementGateOverlayArticle
      ...helpersArticleSections
      entityId
    }
    ...articleSchemaContent @arguments(entityId: $entityId, entityUuid: $entityUuid)
  }
`;

const ArticlePage: RelayComponent<PageProps, articlePageQuery> = ({
  appBarConfiguration,
  preloadedQuery,
}) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const advertisingInfo = useArticleBaseAdvertisingInfo(data.article);
  const currentArticle = useAtomValue(currentArticleAtom);
  const { isAcknowledgementRequired, renderAcknowledgementGateOverlay } =
    useArticleAcknowledgementGateOverlay(data.article);

  useTrackContentInterest(currentArticle);
  useAppBarAdSlot({
    desktop: null,
    mobile: appBarConfiguration?.hasMobileAd
      ? {
          ...advertisingInfo,
          adUnit: "m_banner3",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: data.article.entityId,
            },
          },
        }
      : null,
  });

  return (
    <>
      <ArticleSeo reference={currentArticle ?? data.article} />
      {renderAcknowledgementGateOverlay()}
      {/* TODO: workaround for fixing https://scmp-product-tech.atlassian.net/browse/SCMPWEB-3430, third party should not relied on the application level stuff */}
      <Article $isShowArticle={!isAcknowledgementRequired} className="article-list">
        <List reference={data.article} />
      </Article>
      <ArticleSchema reference={data} />
    </>
  );
};

const List = memo(ArticleList);

ArticlePage.displayName = "ArticlePage";

const getPageProps = (context: NextPageContext) => {
  const appBarVariant = context.query.appBarVariant as AppBarVariant;

  const pageProps: PageProps = {
    animateKey: `article-${context.query.entityId?.toString() ?? ""}`,
    appBarConfiguration: {
      hasMobileAd: appBarVariant !== "posties",
      variant: appBarVariant,
    },
    contentConfiguration: {
      responsivePadding: {
        desktopUp: 32,
        mediumDesktopUp: 52,
        tabletUp: 12,
      },
    },
    headerConfiguration: {
      responsiveFeatures: {
        mobileUp: {
          default: ["dashboard"],
          swapped: ["content-comment", "content-share", "content-bookmark"],
        },
      },
      responsiveLogoVariant: {
        mobileUp: {
          default: "full",
          swapped: "logo",
        },
      },
    },
  };

  return pageProps;
};

export default withRelay(ArticlePage, query, {
  clientSideProps: context => getPageProps(context),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("~/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: context => Promise.resolve(getPageProps(context)),
  variablesFromContext(context) {
    return isString(context.query.entityId) && isString(context.query.entityUuid)
      ? {
          customContents: DefaultCustomContents,
          entityId: context.query.entityId,
          entityUuid: context.query.entityUuid,
        }
      : { customContents: [], entityId: "", entityUuid: "" };
  },
});
